import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useThree } from '@react-three/fiber'
import { useControls } from 'leva'

import { DEBUG_MODE } from '../../config/debug'
import { apriAccount } from '../../config/negozio'

import {
  cartStore, cartOpenStore, searchOpenStore, helpOpenStore,
  watchOpenStore, useStore,
} from '../../store/uiStore'

// ============================================================
// Il quadrante dell'orologio, disegnato SU UNA TEXTURE e applicato
// a un piano figlio del nodo WatchScreen del modello.
//
// PERCHÉ NON UN PANNELLO DOM
// Un rettangolo in coordinate schermo non può seguire una superficie
// che ruota: si posiziona da un calcolo fatto in useFrame, cioè con le
// matrici del frame PRECEDENTE, e per di più resta piatto mentre il
// quadrante si inclina. Qui la faccia è un oggetto della scena: stessa
// matrice dell'orologio, applicata nello stesso istante dalla GPU.
// Posizione, orientamento e scala del piano NON stanno qui: li calcola
// Player dai quattro vertici del quad (vedi lì il perché).
//
// UNA APP ALLA VOLTA, IN FILA SCORREVOLE
// Prima le icone stavano tutte affiancate: erano minuscole e il numero
// di app era limitato dalla larghezza del quadrante. Ora se ne vede una
// grande al centro, con le vicine che sbucano ai lati; si scorre con la
// rotellina, trascinando, o toccando i bordi. AGGIUNGERE UN'APP = UNA
// RIGA NELL'ARRAY QUI SOTTO, nient'altro.
// ============================================================

// Per aggiungere un'app: { id, glifo, nome, apri }. Nessun limite di
// numero — la fila scorre.
const APP = [
  {
    id: 'cerca',
    glifo: '🔍',
    nome: 'Cerca',
    apri: () => { document.exitPointerLock?.(); searchOpenStore.set(true) },
  },
  { id: 'comandi', glifo: '?', nome: 'Comandi', apri: () => helpOpenStore.set(true) },
  { id: 'carrello', glifo: '🛒', nome: 'Carrello', apri: () => cartOpenStore.set(true) },
  // L'account e' una pagina di Shopify, non una finestra nostra: si apre in
  // una scheda a parte (ordini, spedizioni, indirizzi) e il polso si abbassa.
  { id: 'account', glifo: '👤', nome: 'Account', apri: apriAccount },
]

// Il canvas non ha più misure fisse: si adatta alla FORMA del quadrante
// (vedi l'aspetto letto dalla scala della mesh in passo()). Perciò il
// disegno è tutto in frazioni di larghezza e altezza, non in pixel.
const LATO_LUNGO = 512     // risoluzione del lato maggiore

const F = {
  oraY: 0.148,
  oraCorpo: 0.131,
  cy: 0.556,               // altezza della fila di app
  tessera: 0.33,           // lato della tessera, più un extra al centro
  tesseraExtra: 0.102,
  passo: 0.52,             // distanza fra un'app e l'altra, in larghezze
  frecciaX: 0.106,
  pallinoY: 0.920,
}

// Sopra questa frazione dell'altezza non si clicca (è la fascia dell'ora)
const Y_ZONA_F = 0.318

// L'area sensibile DEBORDA dal quadrante. Le frecce sono disegnate vicino
// al bordo, e su un oggetto piccolo e inclinato tenuto in mano mirarle con
// un minimo di scarto porta fuori dal piano: il raggio non interseca più
// nulla e il click si perde. Con questi margini si accetta anche ciò che
// cade appena fuori, riportandolo sul bordo più vicino.
// Provati anche stretti (0.07 / 0.06 / 0.05), aderenti al quadrante: la
// freccia destra tornava difficile, l'area scendeva da 5238 a 2178 px².
const BORDO_SX = 0.16
const BORDO_DX = 0.42
const BORDO_Y = 0.20

// confini fra le tre fasce, in coordinate della texture
const CONFINE_SX = 0.26
const CONFINE_DX = 0.60

// Riquadro di diagnosi (?debug): copre TUTTA l'area sensibile, bordi
// esterni compresi, quindi è più grande del quadrante.
const ZL = 720
const ZA = 560

// Quanto lo schermo deborda dal quad WatchScreen. A 1 coincide con lo
// schermetto modellato in Blender, che è ciò che si vuole: angoli vivi e
// niente invasione della cornice della cassa. Provato anche 1.22, che
// arriva ai bordi della cassa, ma risultava troppo grande.
// Ora che il canvas prende la FORMA del piano, a 1 il disegno riempie il
// quad senza lasciare bande — era quello il difetto di partenza, non la
// dimensione. Lo legge Player in orientaFaccia.
// `ridisegna` la riempie il componente: Player la chiama dopo aver
// orientato il piano, altrimenti il canvas resterebbe della forma che
// aveva PRIMA dell'orientamento (scala 1:1) e il disegno verrebbe
// schiacciato — successo, 512x512 su un piano 0.107 x 0.132.
// `inclinazione` è il pitch (in radianti) a cui la visuale si porta
// quando si alza il polso: la posa del braccio è stata costruita per una
// camera ORIZZONTALE, quindi guardando in alto o in basso il quadrante
// uscirebbe dall'inquadratura. Lo legge Player.
// `riempimento` 1.22: il piano della UI copre il quad WatchScreen (0.108x0.088)
// moltiplicato per questo, cioe' quasi tutta la faccia della cassa
// (0.135x0.115), e resta un filo di cornice. Era rimasto a 1, e con l'occhio
// alzato (OCCHIO_SU in Player.jsx) il quadrante si e' allontanato da 0.36 a
// 0.46: l'area cliccabile era scesa dal 9.5% al 5.7% dello schermo. Il
// riempimento la riporta su, perche' cresce col quadrato.
export const cfgFaccia = { riempimento: 1.22, inclinazione: 0, ridisegna: null }

function rettangolo(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function disegna(ctx, { ora, quantita, scorr, sopra }, W, H) {
  // LE DIMENSIONI SEGUONO IL LATO CORTO, le posizioni restano frazioni di
  // W e H. Quando il canvas ha preso la forma del quadrante, H è passato
  // da lato corto a lato lungo: legare le misure ad H faceva crescere
  // tessere e testi del 45% e la schermata sembrava ingigantita.
  const L = Math.min(W, H)
  ctx.clearRect(0, 0, W, H)
  ctx.save()

  const fondo = ctx.createLinearGradient(0, 0, 0, H)
  fondo.addColorStop(0, '#1b2233')
  fondo.addColorStop(1, '#05070d')
  ctx.fillStyle = fondo
  ctx.fillRect(0, 0, W, H)

  const alone = ctx.createRadialGradient(W / 2, -L * 0.11, 10, W / 2, -L * 0.11, L * 1.1)
  alone.addColorStop(0, 'rgba(96,160,240,0.32)')
  alone.addColorStop(1, 'rgba(96,160,240,0)')
  ctx.fillStyle = alone
  ctx.fillRect(0, 0, W, H)

  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  const font = (frazione, grassetto) =>
    (grassetto ? 'bold ' : '') + Math.round(L * frazione) + 'px Arial, Helvetica, sans-serif'

  ctx.fillStyle = '#dde7f8'
  ctx.font = font(F.oraCorpo, true)
  ctx.shadowColor = 'rgba(120,200,255,0.5)'
  ctx.shadowBlur = L * 0.045
  ctx.fillText(ora, W / 2, H * F.oraY)
  ctx.shadowBlur = 0

  const CY = H * F.cy
  const PASSO = W * F.passo

  // --- la fila di app ---
  APP.forEach((app, i) => {
    const dx = (i - scorr) * PASSO
    if (Math.abs(dx) > PASSO * 1.7) return
    const vicinanza = Math.max(0, 1 - Math.abs(dx) / PASSO)  // 1 al centro
    const lato = L * (F.tessera + F.tesseraExtra * vicinanza)
    const x = W / 2 + dx
    const acceso = sopra === 'apri' && vicinanza > 0.6

    ctx.globalAlpha = 0.20 + 0.80 * vicinanza
    rettangolo(ctx, x - lato / 2, CY - lato / 2, lato, lato, lato * 0.26)
    ctx.fillStyle = acceso ? 'rgba(150,200,255,0.38)' : 'rgba(120,175,255,0.15)'
    ctx.fill()
    ctx.lineWidth = L * 0.009
    ctx.strokeStyle = acceso ? 'rgba(215,238,255,0.68)' : 'rgba(190,225,255,0.25)'
    ctx.stroke()

    ctx.fillStyle = '#f2f7ff'
    ctx.font = (app.id === 'comandi' ? 'bold ' : '') +
      Math.round(lato * 0.48) + 'px Arial, Helvetica, sans-serif'
    ctx.fillText(app.glifo, x, CY + L * 0.011)

    if (app.id === 'carrello' && quantita > 0) {
      const bx = x + lato / 2 - L * 0.028
      const by = CY - lato / 2 + L * 0.028
      ctx.beginPath()
      ctx.arc(bx, by, L * 0.062, 0, Math.PI * 2)
      ctx.fillStyle = '#e3423f'
      ctx.fill()
      ctx.fillStyle = '#fff'
      ctx.font = font(0.077, true)
      ctx.fillText(quantita > 99 ? '99+' : String(quantita), bx, by + 1)
    }

    // il nome compare solo per l'app che sta arrivando al centro
    if (vicinanza > 0.4) {
      ctx.globalAlpha = (vicinanza - 0.4) / 0.6
      ctx.fillStyle = '#cbdcf3'
      ctx.font = font(0.082)
      ctx.fillText(app.nome, x, CY + lato / 2 + L * 0.085)
    }
  })
  ctx.globalAlpha = 1

  // --- frecce ai lati: dicono che si scorre ---
  const freccia = (x, segno, evidenziata) => {
    if (evidenziata) {
      rettangolo(ctx, x - L * 0.093, CY - L * 0.148, L * 0.186, L * 0.295, L * 0.068)
      ctx.fillStyle = 'rgba(150,200,255,0.20)'
      ctx.fill()
    }
    ctx.globalAlpha = evidenziata ? 1 : 0.5
    ctx.strokeStyle = '#e2eeff'
    ctx.lineWidth = L * 0.026
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(x + segno * L * 0.032, CY - L * 0.085)
    ctx.lineTo(x - segno * L * 0.032, CY)
    ctx.lineTo(x + segno * L * 0.032, CY + L * 0.085)
    ctx.stroke()
    ctx.globalAlpha = 1
  }
  // sempre attive: la fila è ciclica, non ci sono capolinea
  freccia(W * F.frecciaX, 1, sopra === 'prec')
  freccia(W * (1 - F.frecciaX), -1, sopra === 'succ')

  // --- pallini di posizione ---
  const passoPallini = L * 0.056
  const x0 = W / 2 - ((APP.length - 1) * passoPallini) / 2
  APP.forEach((_, i) => {
    const vicinanza = Math.max(0, 1 - Math.abs(i - scorr))
    ctx.beginPath()
    ctx.arc(x0 + i * passoPallini, H * F.pallinoY, L * (0.011 + 0.006 * vicinanza), 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(200,225,255,' + (0.25 + 0.7 * vicinanza) + ')'
    ctx.fill()
  })

  ctx.restore()
}

// Disegna il wireframe delle zone sensibili. Il canvas rappresenta
// l'INTERA area utile: u va da -bordoSx a 1+bordoDx, v da -bordoY a
// 1+bordoY. Il rettangolo del quadrante vero è quello interno.
function disegnaZone(ctx, { sopra, cfg }) {
  ctx.clearRect(0, 0, ZL, ZA)
  const U = 1 + cfg.bordoSx + cfg.bordoDx
  const V = 1 + 2 * cfg.bordoY
  const cx = (u) => ((u + cfg.bordoSx) / U) * ZL
  const cy = (v) => (1 - (v + cfg.bordoY) / V) * ZA
  const vZona = 1 - Y_ZONA_F              // sopra questa riga: insensibile

  // fascia evidenziata sotto il puntatore
  if (sopra) {
    const da = sopra === 'prec' ? -cfg.bordoSx
             : sopra === 'apri' ? cfg.confineSx : cfg.confineDx
    const a = sopra === 'prec' ? cfg.confineSx
            : sopra === 'apri' ? cfg.confineDx : 1 + cfg.bordoDx
    ctx.fillStyle = 'rgba(90,255,140,0.20)'
    ctx.fillRect(cx(da), cy(vZona), cx(a) - cx(da), cy(-cfg.bordoY) - cy(vZona))
  }

  // limite esterno dell'area sensibile
  ctx.setLineDash([14, 10])
  ctx.lineWidth = 5
  ctx.strokeStyle = 'rgba(255,90,90,0.95)'
  ctx.strokeRect(3, 3, ZL - 6, ZA - 6)
  ctx.setLineDash([])

  // bordo del quadrante vero
  ctx.lineWidth = 4
  ctx.strokeStyle = 'rgba(255,255,255,0.85)'
  ctx.strokeRect(cx(0), cy(1), cx(1) - cx(0), cy(0) - cy(1))

  // riga oltre la quale non si clicca (zona dell'ora) e confini fra fasce
  ctx.lineWidth = 4
  ctx.strokeStyle = 'rgba(120,220,255,0.95)'
  ctx.beginPath()
  ctx.moveTo(cx(-cfg.bordoSx), cy(vZona))
  ctx.lineTo(cx(1 + cfg.bordoDx), cy(vZona))
  ctx.stroke()

  ctx.strokeStyle = 'rgba(120,255,150,0.95)'
  for (const u of [cfg.confineSx, cfg.confineDx]) {
    ctx.beginPath()
    ctx.moveTo(cx(u), cy(vZona))
    ctx.lineTo(cx(u), cy(-cfg.bordoY))
    ctx.stroke()
  }

  // etichette
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = 'bold 30px Arial, Helvetica, sans-serif'
  ctx.fillStyle = 'rgba(180,255,200,0.95)'
  const meta = (a, b) => (cx(a) + cx(b)) / 2
  const yEt = (cy(vZona) + cy(-cfg.bordoY)) / 2
  ctx.fillText('prec', meta(-cfg.bordoSx, cfg.confineSx), yEt)
  ctx.fillText('apri', meta(cfg.confineSx, cfg.confineDx), yEt)
  ctx.fillText('succ', meta(cfg.confineDx, 1 + cfg.bordoDx), yEt)

  ctx.font = '24px Arial, Helvetica, sans-serif'
  ctx.fillStyle = 'rgba(255,150,150,0.95)'
  ctx.fillText('bordi  sx ' + cfg.bordoSx.toFixed(2) +
               '   dx ' + cfg.bordoDx.toFixed(2) +
               '   y ' + cfg.bordoY.toFixed(2), ZL / 2, 30)
  ctx.fillStyle = 'rgba(150,255,180,0.95)'
  ctx.fillText('confini  ' + cfg.confineSx.toFixed(2) +
               '  /  ' + cfg.confineDx.toFixed(2), ZL / 2, ZA - 26)
}

const orario = () => {
  const d = new Date()
  return String(d.getHours()).padStart(2, '0') + ':' +
         String(d.getMinutes()).padStart(2, '0')
}

const OrologioFaccia = forwardRef(function OrologioFaccia(_props, ref) {
  const camera = useThree((s) => s.camera)
  const gl = useThree((s) => s.gl)
  const mio = useRef(null)
  const cart = useStore(cartStore)
  const aperto = useStore(watchOpenStore)
  const [ora, setOra] = useState(orario)

  // Stato del quadrante tenuto in un ref e non in useState: scorrimento
  // e evidenziazione cambiano di continuo mentre si scorre, e passare
  // da React vorrebbe dire un render a ogni frame dell'animazione.
  // Tarabili dal vivo in ?debug: le zone sensibili non si vedono, e
  // dedurle dai numeri è un lavoro alla cieca. Il riquadro qui sotto le
  // disegna, questi cursori le muovono.
  const cfg = useControls('Orologio', {
    mostraZone: false,
    riempimento: { value: cfgFaccia.riempimento, min: 0.8, max: 1.45, step: 0.01 },
    // Alzando il polso la visuale si porta a questo pitch. Era 0 (orizzonte)
    // quando l'occhio stava 0.25 sopra la testa e il quadrante cadeva a 18
    // gradi sotto il centro. Ora l'occhio sta piu' in alto (OCCHIO_SU in
    // Player.jsx) e il polso alzato gli e' piu' in basso: senza inclinare, il
    // quadrante finisce sotto il bordo dello schermo. MISURATO quanti gradi
    // sotto l'orizzonte cade il quadrante: 18.5 a occhioSu 0.25 (com'era da
    // sempre), 29.5 a 0.35, 42.3 a 0.45. L'inclinazione e' semplicemente la
    // differenza, cosi' il quadrante torna dov'era rispetto al centro dello
    // schermo. Toccando occhioSu in Player.jsx, va rimisurata.
    // Da -11 a -20 (chiesto: «il braccio un po' piu' in alto quando premo Q»).
    // MISURATO col quadrante proiettato sullo schermo: a -11 cadeva a -0.50 in
    // coordinate schermo, cioe' a meta' strada fra il centro e il bordo basso —
    // 19.3 gradi sotto il centro della vista, che col FOV verticale di 70 fa
    // 30.3 gradi sotto l'orizzonte. Ogni grado di inclinazione in piu' e' un
    // grado guadagnato, quindi -20 lo porta a 10.3 sotto il centro, circa -0.26.
    // Non oltre: la posa del braccio e' costruita per una camera orizzontale, e
    // piu' si inclina piu' si guarda per terra invece che il polso.
    inclinazioneGradi: { value: -20, min: -60, max: 25, step: 1 },
    bordoSx: { value: BORDO_SX, min: 0, max: 0.9, step: 0.01 },
    bordoDx: { value: BORDO_DX, min: 0, max: 0.9, step: 0.01 },
    bordoY: { value: BORDO_Y, min: 0, max: 0.9, step: 0.01 },
    confineSx: { value: CONFINE_SX, min: 0.05, max: 0.45, step: 0.01 },
    confineDx: { value: CONFINE_DX, min: 0.5, max: 0.95, step: 0.01 },
  }, { collapsed: true })

  const st = useRef({ ora, quantita: 0, indice: 0, scorr: 0, sopra: null, t: 0 })
  st.current.ora = ora
  st.current.quantita = cart?.totalQuantity || 0
  // i gestori degli eventi hanno deps [camera, gl]: leggerebbero valori
  // vecchi, quindi la configurazione passa dal ref
  st.current.cfg = cfg
  cfgFaccia.riempimento = cfg.riempimento
  cfgFaccia.inclinazione = (cfg.inclinazioneGradi * Math.PI) / 180

  const canvas = useMemo(() => {
    const c = document.createElement('canvas')
    c.width = LATO_LUNGO
    c.height = Math.round(LATO_LUNGO * 0.8)
    return c
  }, [])

  const texture = useMemo(() => {
    const t = new THREE.CanvasTexture(canvas)
    t.colorSpace = THREE.SRGBColorSpace
    t.anisotropy = 4
    return t
  }, [canvas])

  const canvasZone = useMemo(() => {
    const c = document.createElement('canvas')
    c.width = ZL
    c.height = ZA
    return c
  }, [])
  const textureZone = useMemo(() => {
    const t = new THREE.CanvasTexture(canvasZone)
    t.colorSpace = THREE.SRGBColorSpace
    return t
  }, [canvasZone])

  useEffect(() => () => texture.dispose(), [texture])
  useEffect(() => () => textureZone.dispose(), [textureZone])

  // Un solo disegnatore, richiamabile da chiunque. Continua a chiamarsi
  // da solo finché lo scorrimento non si è assestato, poi si ferma: non
  // c'è nessun ridisegno a riposo.
  const pendente = useRef(0)
  const disegnaRef = useRef(null)
  useEffect(() => {
    const ctx = canvas.getContext('2d')
    const ctxZone = canvasZone.getContext('2d')
    const passo = () => {
      const s = st.current
      const adesso = performance.now()
      const dt = s.t ? Math.min(0.05, (adesso - s.t) / 1000) : 0.016
      s.t = adesso
      const scarto = s.indice - s.scorr
      if (Math.abs(scarto) < 0.002) s.scorr = s.indice
      else s.scorr += scarto * (1 - Math.exp(-dt * 13))
      // Il canvas prende la forma del piano: se restasse a proporzioni
      // fisse il disegno andrebbe iscritto dentro il quadrante e
      // resterebbero due bande vuote ai lati (era così).
      const m = mio.current
      if (m) {
        const rapporto = m.scale.x / m.scale.y
        const W = rapporto >= 1 ? LATO_LUNGO : Math.round(LATO_LUNGO * rapporto)
        const H = rapporto >= 1 ? Math.round(LATO_LUNGO / rapporto) : LATO_LUNGO
        if (canvas.width !== W || canvas.height !== H) {
          canvas.width = W
          canvas.height = H
          texture.dispose()   // la texture va ricreata: cambia la dimensione
        }
      }
      disegna(ctx, s, canvas.width, canvas.height)
      texture.needsUpdate = true
      if (DEBUG_MODE && s.cfg?.mostraZone) {
        disegnaZone(ctxZone, s)
        textureZone.needsUpdate = true
      }
      pendente.current = s.scorr === s.indice ? 0 : requestAnimationFrame(passo)
    }
    const chiedi = () => {
      if (!pendente.current) {
        st.current.t = 0
        pendente.current = requestAnimationFrame(passo)
      }
    }
    disegnaRef.current = chiedi
    cfgFaccia.ridisegna = chiedi
    chiedi()
    return () => {
      cfgFaccia.ridisegna = null
      if (pendente.current) cancelAnimationFrame(pendente.current)
      pendente.current = 0
    }
  }, [canvas, texture, canvasZone, textureZone])

  // ridisegno al minuto, al cambio carrello e a ogni ritocco dei cursori
  useEffect(() => { disegnaRef.current?.() }, [ora, cart, cfg])

  useEffect(() => {
    const id = setInterval(() => setOra(orario()), 15000)
    return () => clearInterval(id)
  }, [])

  // Abbassando il polso il pointerout può non arrivare: senza questo il
  // cursore resterebbe a manina sul resto del negozio.
  useEffect(() => {
    if (aperto) return
    st.current.sopra = null
    document.body.style.cursor = ''
    disegnaRef.current?.()
  }, [aperto])

  // Puntamento fatto a mano invece che con gli handler di R3F: questa
  // mesh è innestata nel nodo dell'orologio con createPortal, e per il
  // sistema di eventi di R3F è terreno incerto. Un raycast su UN solo
  // oggetto costa niente ed è sotto il nostro controllo.
  useEffect(() => {
    const el = gl.domElement
    const rc = new THREE.Raycaster()
    const ndc = new THREE.Vector2()

    // uv -> zona del quadrante. Le fasce sono a piena altezza e molto
    // più grandi delle grafiche: mirare al pixel su un oggetto tenuto in
    // mano è scomodo.
    // Sono ASIMMETRICHE apposta: il quadrante è inclinato, il suo lato
    // destro è quello LONTANO e la prospettiva lo comprime. A parità di
    // uv lì c'è molta meno superficie sullo schermo (misurato: 1632 px²
    // contro 2608 a sinistra), quindi gli si dà una fetta più larga.
    const zonaDa = (uv) => {
      if (!uv) return null
      if (1 - uv.y < Y_ZONA_F) return null
      const c = st.current.cfg
      if (uv.x < c.confineSx) return 'prec'
      if (uv.x > c.confineDx) return 'succ'
      return 'apri'
    }
    // Invece di intersecare la MESH si interseca il suo PIANO INFINITO:
    // così si può accettare anche un colpo appena fuori dal quadrante e
    // riportarlo sul bordo, cosa che con intersectObject sarebbe
    // impossibile (fuori dal rettangolo non c'è intersezione).
    const piano = new THREE.Plane()
    const normale = new THREE.Vector3()
    const punto = new THREE.Vector3()
    const uv = { x: 0, y: 0 }
    const colpito = (e) => {
      const m = mio.current
      if (!m || !m.visible || !watchOpenStore.get()) return null
      const r = el.getBoundingClientRect()
      ndc.set(
        ((e.clientX - r.left) / r.width) * 2 - 1,
        -((e.clientY - r.top) / r.height) * 2 + 1
      )
      rc.setFromCamera(ndc, camera)
      m.getWorldPosition(punto)
      normale.set(0, 0, 1).transformDirection(m.matrixWorld)
      piano.setFromNormalAndCoplanarPoint(normale, punto)
      if (!rc.ray.intersectPlane(piano, punto)) return null
      m.worldToLocal(punto)               // il piano va da -0.5 a +0.5
      const ux = punto.x + 0.5
      const uy = punto.y + 0.5
      const c = st.current.cfg
      if (ux < -c.bordoSx || ux > 1 + c.bordoDx) return null
      if (uy < -c.bordoY || uy > 1 + c.bordoY) return null
      uv.x = Math.min(1, Math.max(0, ux))
      uv.y = Math.min(1, Math.max(0, uy))
      return zonaDa(uv)
    }
    // Ciclica: dall'ultima si torna alla prima. Così nessuna app è
    // raggiungibile SOLO passando dalla freccia destra, che sul quadrante
    // inclinato resta il bersaglio più scomodo.
    const scorri = (verso) => {
      const s = st.current
      s.indice = (s.indice + verso + APP.length) % APP.length
      disegnaRef.current?.()
    }

    const suMovimento = (e) => {
      const z = colpito(e)
      if (z === st.current.sopra) return
      st.current.sopra = z
      document.body.style.cursor = z ? 'pointer' : ''
      disegnaRef.current?.()
    }

    // rotellina: i trackpad mandano tanti eventi minuscoli, quindi si
    // accumula e si scatta oltre una soglia
    let accumulo = 0
    const suRotella = (e) => {
      if (!colpito(e)) return
      e.preventDefault()
      accumulo += e.deltaY + e.deltaX
      if (Math.abs(accumulo) < 40) return
      scorri(accumulo > 0 ? 1 : -1)
      accumulo = 0
    }

    // trascinamento (indispensabile su touch, dove la rotellina non c'è)
    let partenza = null
    let ultimoTrascina = 0
    const suGiu = (e) => { partenza = colpito(e) ? e.clientX : null }
    const suSu = (e) => {
      if (partenza === null) return
      const dx = e.clientX - partenza
      partenza = null
      if (Math.abs(dx) < 24) return
      ultimoTrascina = performance.now()
      scorri(dx < 0 ? 1 : -1)
    }
    const suClick = (e) => {
      // un trascinamento produce anche un click: va ignorato
      if (performance.now() - ultimoTrascina < 300) return
      const z = colpito(e)
      if (!z) return
      if (z === 'prec') scorri(-1)
      else if (z === 'succ') scorri(1)
      else APP[st.current.indice].apri()
    }

    // Col polso alzato il personaggio non cammina (isMovementBlocked),
    // quindi i tasti direzionali sono liberi: sono il modo più comodo
    // di scorrere, molto più che centrare una freccia sul quadrante.
    const suTasto = (e) => {
      if (!watchOpenStore.get()) return
      const b = e.target
      if (b instanceof HTMLElement &&
          (b.tagName === 'INPUT' || b.tagName === 'TEXTAREA' || b.isContentEditable)) return
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') scorri(-1)
      else if (e.code === 'ArrowRight' || e.code === 'KeyD') scorri(1)
      else if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault()
        APP[st.current.indice].apri()
      }
    }

    if (DEBUG_MODE) {
      window.__orologio = {
        mesh: () => mio.current,
        stato: () => st.current,
        prova: (x, y) => colpito({ clientX: x, clientY: y }),
        uv: (x, y) => { const z = colpito({ clientX: x, clientY: y })
          return z ? { zona: z, x: +uv.x.toFixed(3), y: +uv.y.toFixed(3) } : null },
      }
    }

    window.addEventListener('keydown', suTasto)
    el.addEventListener('pointermove', suMovimento)
    el.addEventListener('pointerdown', suGiu)
    el.addEventListener('pointerup', suSu)
    el.addEventListener('click', suClick)
    el.addEventListener('wheel', suRotella, { passive: false })
    return () => {
      window.removeEventListener('keydown', suTasto)
      el.removeEventListener('pointermove', suMovimento)
      el.removeEventListener('pointerdown', suGiu)
      el.removeEventListener('pointerup', suSu)
      el.removeEventListener('click', suClick)
      el.removeEventListener('wheel', suRotella)
    }
  }, [camera, gl])

  return (
    <mesh
      ref={(v) => {
        mio.current = v
        if (typeof ref === 'function') ref(v)
        else if (ref) ref.current = v
      }}
      name="OrologioFaccia"
      visible={false}
    >
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial
        map={texture}
        transparent
        opacity={0}
        depthWrite={false}
        toneMapped={false}
      />

      {/* Wireframe delle zone: FIGLIO del quadrante, così ne eredita
          posizione e inclinazione senza doverle ricalcolare. È più
          grande di lui perché l'area sensibile deborda dai bordi.
          depthTest spento: deve vedersi anche dietro la mano. */}
      {DEBUG_MODE && cfg.mostraZone && (
        <mesh
          position={[(cfg.bordoDx - cfg.bordoSx) / 2, 0, 0.004]}
          scale={[1 + cfg.bordoSx + cfg.bordoDx, 1 + 2 * cfg.bordoY, 1]}
          renderOrder={999}
        >
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial
            map={textureZone}
            transparent
            depthTest={false}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      )}
    </mesh>
  )
})

export default OrologioFaccia
